import { useEffect, useState } from "react";
import { ApiError } from "../../common/api/ApiClient";
import { useApiClient } from "../../common/hooks/useApiClient";

export interface AdminProfile {
  inviter_email: string;
  inviter_name: string | null;
  type: string;
  permissions: string | null;
  invitesAvailable: number;
  refilledAt: number | null;
  expiresAt: number | null;
}

export interface ProfileSearchResult {
  inviter_email: string;
  inviter_name: string | null;
  type: string;
  permissions: string | null;
  invitesAvailable: number;
  refilledAt: number | null;
  expiresAt: number | null;
}

export const useAdminInvitations = () => {
  const [admins, setAdmins] = useState<AdminProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchedProfile, setSearchedProfile] =
    useState<ProfileSearchResult | null>(null);
  const [setCountLoading, setSetCountLoading] = useState(false);
  const [setCountError, setSetCountError] = useState<string | null>(null);
  const [createAdminLoading, setCreateAdminLoading] = useState(false);
  const [createAdminError, setCreateAdminError] = useState<string | null>(null);
  const apiClient = useApiClient();
  const basePath = "/invite-admin";

  useEffect(() => {
    getAdmins();
  }, []);

  const getAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<AdminProfile[]>(
        basePath + "/admins",
      );
      setAdmins(response.data);
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const createAdmin = async (email: string) => {
    try {
      setCreateAdminLoading(true);
      setCreateAdminError(null);
      const response = await apiClient.post<AdminProfile>(
        basePath + "/admins",
        {
          email,
        },
      );
      setAdmins([...admins, response.data]);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage = apiError.data?.detail?.message || apiError.message;
      setCreateAdminError(errorMessage);
      return null;
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const deleteAdmin = async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      await apiClient.delete(basePath + "/admins/" + encodeURIComponent(email));
      setAdmins(admins.filter((admin) => admin.inviter_email !== email));
      return true;
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const searchProfile = async (email: string) => {
    try {
      setSearchLoading(true);
      setSearchError(null);
      setSearchedProfile(null);
      const response = await apiClient.post<ProfileSearchResult>(
        basePath + "/profile/search",
        { email },
      );
      setSearchedProfile(response.data);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage = apiError.data?.detail?.message || apiError.message;
      setSearchError(errorMessage);
      return null;
    } finally {
      setSearchLoading(false);
    }
  };

  const setInvitationCount = async (email: string, count: number) => {
    try {
      setSetCountLoading(true);
      setSetCountError(null);
      const response = await apiClient.post<ProfileSearchResult>(
        basePath + "/profile/set-invitations",
        { email, count },
      );
      setSearchedProfile(response.data);
      // Update admin list if this user is in it
      setAdmins(
        admins.map((admin) =>
          admin.inviter_email === email
            ? { ...admin, invitesAvailable: count }
            : admin,
        ),
      );
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage = apiError.data?.detail?.message || apiError.message;
      setSetCountError(errorMessage);
      return null;
    } finally {
      setSetCountLoading(false);
    }
  };

  return {
    admins,
    setAdmins,
    loading,
    error,
    getAdmins,
    createAdmin,
    createAdminLoading,
    createAdminError,
    deleteAdmin,
    searchProfile,
    searchLoading,
    searchError,
    searchedProfile,
    setSearchedProfile,
    setInvitationCount,
    setCountLoading,
    setCountError,
  };
};
